import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Trash2 } from "lucide-react";
import { API } from "./_apiConfig";

const ContactsManagement = () => {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (contactId) => {
    try {
      const response = await fetch(`${API}/admin/contacts/${contactId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        fetchContacts();
      }
    } catch (error) {
      console.error("Error marking contact as read:", error);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm("Supprimer ce message ?")) return;

    try {
      const response = await fetch(`${API}/admin/contacts/${contactId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Message supprimé");
        fetchContacts();
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="admin-contacts">
      <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
        Messages de Contact
      </h2>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : contacts.length > 0 ? (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <Card key={contact.id} className={!contact.read ? "border-primary" : ""}>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-foreground">
                        {contact.name}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        ({contact.email})
                      </span>
                      {!contact.read && <Badge>Nouveau</Badge>}
                    </div>
                    <p className="font-medium text-foreground mb-2">
                      {contact.subject}
                    </p>
                    <p className="text-muted-foreground">{contact.message}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(contact.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!contact.read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleMarkRead(contact.id);
                        }}
                        className="text-green-500 hover:bg-green-500/10"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(contact.id);
                      }}
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Aucun message de contact.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContactsManagement;
